import type { Release, ReleaseAdapterState } from "@packages/commons";
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  adapterDefinitions,
  getAnswers,
  type AdapterApplyAnswers,
  type AdapterDefinition,
} from "@/features/adapters/model";
import {
  clearAdapterWorkflowOpenState,
  setAdapterWorkflowOpenState,
} from "@/features/adapters/openState";
import {
  createAdapterWorkflow,
  getReleaseAdapterWorkflow,
  sanitizeAdapterAnswers,
  addVisitedStep,
  getAdapterVisitedSteps,
  setReleaseAdapterWorkflow,
  type AdapterWorkflowStepStatus,
  type AdapterWorkflowProgress,
} from "@/features/adapters/persistence";
import { createAdapterSaveQueue, type AdapterSaveQueue } from "@/features/adapters/saveQueue";
import { buildAdapterWizardSteps } from "@/features/adapters/wizard";
import { AdapterMappingPreviewProvider } from "./AdapterMappingPreviewContext";

interface AdapterWorkflowState {
  releaseId?: string;
  definitionId?: string;
  isOpen: boolean;
  autoFollow: boolean;
  step: number;
  stepKey?: string;
  stepStatus: AdapterWorkflowStepStatus[];
  stepErrorsByKey: Record<string, string[]>;
  confirmations: Record<string, boolean>;
  manualStepCompletions: Record<string, boolean>;
  visitedSteps: string[];
  connectionTests?: AdapterWorkflowProgress["connectionTests"];
  answers?: AdapterApplyAnswers;
}

interface AdapterWorkflowContextValue extends AdapterWorkflowState {
  startAdapter: (releaseId: string, definitionId: string, answers: AdapterApplyAnswers) => void;
  resumeAdapter: (releaseId: string, definitionId: string, answers: AdapterApplyAnswers, persisted: AdapterWorkflowProgress) => void;
  resetAdapter: () => void;
  openAdapter: () => void;
  openAdapterAtStep: (definitionId: string, stepKey: string) => boolean;
  closeAdapter: () => void;
  setStep: (step: number, stepKey?: string) => void;
  setAnswers: (answers: AdapterApplyAnswers) => void;
  setAutoFollow: (autoFollow: boolean) => void;
  /** Records that the user has been on a step. It doesn't change the step's state. */
  markStepVisited: (stepKey: string) => void;
  setStepStatuses: (stepStatus: AdapterWorkflowStepStatus[]) => void;
  setStepErrors: (stepKey: string, errors: string[]) => void;
  setConfirmation: (confirmationId: string, confirmed: boolean) => void;
  setManualStepCompletion: (stepKey: string, completed: boolean) => void;
  setConnectionTests: (tests: AdapterWorkflowProgress["connectionTests"]) => void;
  /**
   * The one queue every write of a release's `adapters` array goes through. Each write replaces the whole
   * array, so writes must never overlap and each must start from the previous one's result.
   */
  saveQueue: AdapterSaveQueue<Release>;
  /**
   * Saves adapter records. `build` runs inside the queue against the latest release, so it never works from
   * a stale copy. Rejects if the write fails, and resolves `undefined` when nothing can persist.
   */
  saveAdapters: (build: (current: Release) => ReleaseAdapterState[]) => Promise<Release | undefined>;
}

const initialState: AdapterWorkflowState = {
  isOpen: false,
  autoFollow: true,
  step: 0,
  stepStatus: [],
  stepErrorsByKey: {},
  confirmations: {},
  manualStepCompletions: {},
  visitedSteps: [],
};

const AdapterWorkflowContext = createContext<AdapterWorkflowContextValue | undefined>(undefined);

interface AdapterWorkflowProviderProps extends PropsWithChildren {
  definitions?: AdapterDefinition[];
  release?: Release;
  /** Writes the given adapter records and resolves the release the server now has. */
  writeAdapters?: (adapters: ReleaseAdapterState[], current: Release) => Promise<Release>;
  onSaveError?: (error: unknown) => void;
}

export const AdapterWorkflowProvider = ({ children, definitions = adapterDefinitions, release, writeAdapters, onSaveError }: AdapterWorkflowProviderProps) => {
  const [state, setState] = useState<AdapterWorkflowState>(initialState);
  const saveQueue = useMemo(() => createAdapterSaveQueue<Release>(), []);

  // Seeds the queue and re-seeds it when the release changes. After that, the queue's own results are the
  // authority on `adapters`; the working release the panel holds refreshes it with anything newer.
  useEffect(() => {
    const latest = saveQueue.current();
    if (release && (!latest || latest.id !== release.id)) saveQueue.sync(release);
  }, [release, saveQueue]);

  const saveAdapters = useCallback(
    async (build: (current: Release) => ReleaseAdapterState[]) => {
      if (!writeAdapters) return undefined;
      if (!saveQueue.current() && release) saveQueue.sync(release);
      try {
        return await saveQueue.save((current) => writeAdapters(build(current), current));
      } catch (error) {
        onSaveError?.(error);
        throw error;
      }
    },
    [onSaveError, release, saveQueue, writeAdapters],
  );

  const value = useMemo<AdapterWorkflowContextValue>(
    () => ({
      ...state,
      startAdapter: (releaseId, definitionId, answers) => {
        setAdapterWorkflowOpenState({ releaseId, definitionId });
        setState((current) => ({
          releaseId,
          definitionId,
          answers,
          isOpen: true,
          autoFollow: current.autoFollow,
          step: 0,
          stepKey: "introduction",
          stepStatus: [],
          stepErrorsByKey: {},
          confirmations: {},
          manualStepCompletions: {},
          visitedSteps: [],
        }));
      },
      resumeAdapter: (releaseId, definitionId, answers, persisted) => {
        setAdapterWorkflowOpenState({ releaseId, definitionId });
        setState((current) => ({
          releaseId,
          definitionId,
          answers,
          isOpen: true,
          autoFollow: current.autoFollow,
          step: persisted.step,
          stepKey: persisted.stepKey,
          stepStatus: persisted.stepStatus ?? [],
          stepErrorsByKey: {},
          confirmations: persisted.confirmations,
          manualStepCompletions: persisted.manualStepCompletions ?? {},
          visitedSteps: getAdapterVisitedSteps(persisted),
          connectionTests: persisted.connectionTests,
        }));
      },
      resetAdapter: () => {
        clearAdapterWorkflowOpenState();
        setState((current) => ({ ...initialState, autoFollow: current.autoFollow }));
      },
      openAdapter: () => {
        if (state.releaseId && state.definitionId) {
          setAdapterWorkflowOpenState({ releaseId: state.releaseId, definitionId: state.definitionId });
        }
        setState((current) => ({ ...current, isOpen: true }));
      },
      openAdapterAtStep: (definitionId, stepKey) => {
        if (!release) return false;
        const definition = definitions.find((candidate) => candidate.id === definitionId);
        if (!definition) return false;

        const isActive = state.releaseId === release.id && state.definitionId === definitionId;
        const answers = (isActive && state.answers
          ? state.answers
          : sanitizeAdapterAnswers(definition, getAnswers(release, definition))) as AdapterApplyAnswers;
        const steps = buildAdapterWizardSteps(definition, answers, release);
        const step = steps.findIndex((candidate) => candidate.key === stepKey);
        if (step < 0) return false;

        const updatedAt = new Date().toISOString();
        // The step to open, layered over whatever progress the given release holds and the live state.
        const workflowAt = (source: Release): AdapterWorkflowProgress => {
          const persisted = getReleaseAdapterWorkflow(source, definitionId) ?? createAdapterWorkflow();
          const currentWorkflow = isActive
            ? {
                ...persisted,
                step: state.step,
                stepKey: state.stepKey ?? persisted.stepKey,
                stepStatus: state.stepStatus,
                confirmations: state.confirmations,
                manualStepCompletions: state.manualStepCompletions,
                visitedSteps: state.visitedSteps,
              }
            : persisted;
          return { ...currentWorkflow, step, stepKey, stepStatus: currentWorkflow.stepStatus ?? [], updatedAt };
        };
        const nextWorkflow = workflowAt(release);

        setAdapterWorkflowOpenState({ releaseId: release.id, definitionId });
        setState((current) => ({
          releaseId: release.id,
          definitionId,
          answers,
          isOpen: true,
          autoFollow: current.autoFollow,
          step,
          stepKey,
          stepStatus: nextWorkflow.stepStatus,
          stepErrorsByKey: isActive ? state.stepErrorsByKey : {},
          confirmations: nextWorkflow.confirmations,
          manualStepCompletions: nextWorkflow.manualStepCompletions ?? {},
          visitedSteps: getAdapterVisitedSteps(nextWorkflow),
          connectionTests: nextWorkflow.connectionTests,
        }));
        void saveAdapters((current) => setReleaseAdapterWorkflow(current, definition, workflowAt(current), answers)).catch(() => undefined);
        return true;
      },
      closeAdapter: () => {
        clearAdapterWorkflowOpenState();
        setState((current) => ({ ...current, isOpen: false }));
      },
      setStep: (step, stepKey) => setState((current) => ({ ...current, step, stepKey })),
      setAnswers: (answers) => setState((current) => ({ ...current, answers })),
      setAutoFollow: (autoFollow) => setState((current) => ({ ...current, autoFollow })),
      markStepVisited: (stepKey) => setState((current) => ({
        ...current,
        visitedSteps: addVisitedStep(current.visitedSteps, stepKey),
      })),
      setStepStatuses: (stepStatus) => setState((current) => ({ ...current, stepStatus })),
      setStepErrors: (stepKey, errors) => setState((current) => {
        const stepErrorsByKey = { ...current.stepErrorsByKey };
        if (errors.length > 0) stepErrorsByKey[stepKey] = [...new Set(errors)];
        else delete stepErrorsByKey[stepKey];
        return { ...current, stepErrorsByKey };
      }),
      setConfirmation: (confirmationId, confirmed) => setState((current) => ({
        ...current,
        confirmations: { ...current.confirmations, [confirmationId]: confirmed },
      })),
      setManualStepCompletion: (stepKey, completed) => setState((current) => {
        const manualStepCompletions = { ...current.manualStepCompletions };
        if (completed) manualStepCompletions[stepKey] = true;
        else delete manualStepCompletions[stepKey];
        return { ...current, manualStepCompletions };
      }),
      setConnectionTests: (connectionTests) => setState((current) => ({ ...current, connectionTests })),
      saveQueue,
      saveAdapters,
    }),
    [definitions, release, saveAdapters, saveQueue, state],
  );

  return (
    <AdapterWorkflowContext.Provider value={value}>
      <AdapterMappingPreviewProvider>{children}</AdapterMappingPreviewProvider>
    </AdapterWorkflowContext.Provider>
  );
};

export const useAdapterWorkflow = (): AdapterWorkflowContextValue => {
  const context = useContext(AdapterWorkflowContext);
  if (!context) throw new Error("useAdapterWorkflow must be used inside AdapterWorkflowProvider.");
  return context;
};

export const useOptionalAdapterWorkflow = (): AdapterWorkflowContextValue | undefined =>
  useContext(AdapterWorkflowContext);
